import React, { useState, useEffect, useRef } from 'react';

export interface TricolorBrandWordmarkProps {
  /** Size variant for typography */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'hero';
  /** Whether the wordmark responds to phone tilt (gyro) and mouse hover */
  interactive?: boolean;
  /** Custom extra classes */
  className?: string;
  /** Whether to show a 3-part Mexican tricolor indicator bar below */
  showAccentBar?: boolean;
  /** Brand text to display (defaults to 'NINPO SNACKS') */
  text?: string;
}

const SIZE_CLASSES: Record<NonNullable<TricolorBrandWordmarkProps['size']>, string> = {
  sm: 'text-xs sm:text-sm font-black tracking-widest',
  md: 'text-sm sm:text-lg font-black tracking-widest',
  lg: 'text-xl sm:text-2xl font-black tracking-widest',
  xl: 'text-3xl sm:text-4xl font-black tracking-widest',
  hero: 'text-4xl sm:text-6xl font-black tracking-tight',
};

export const TricolorBrandWordmark: React.FC<TricolorBrandWordmarkProps> = ({
  size = 'md',
  interactive = true,
  className = '',
  showAccentBar = false,
  text = 'NINPO SNACKS',
}) => {
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const [angle, setAngle] = useState<number>(90);
  const [glareStop, setGlareStop] = useState<number>(50);
  const [tilt, setTilt] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    if (!interactive) return;

    // Respect user accessibility preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    // 1. Mobile Gyroscope / Device Orientation Listener
    const handleOrientation = (e: DeviceOrientationEvent) => {
      if (typeof e.gamma === 'number' && e.gamma !== null) {
        // gamma is left-to-right tilt in degrees (-90 to +90)
        const clampedGamma = Math.max(-45, Math.min(45, e.gamma));
        const normalized = clampedGamma / 45; // -1 to +1

        const nextAngle = 90 + normalized * 35; // 55deg to 125deg
        const nextGlare = 50 + normalized * 25; // 25% to 75%
        setAngle(nextAngle);
        setGlareStop(nextGlare);

        if (typeof e.beta === 'number' && e.beta !== null) {
          const clampedBeta = Math.max(0, Math.min(90, e.beta));
          const normalizedY = (clampedBeta - 45) / 45; // -1 to +1
          setTilt({ x: normalized, y: normalizedY });
        } else {
          setTilt((prev) => ({ ...prev, x: normalized }));
        }
      }
    };

    window.addEventListener('deviceorientation', handleOrientation);
    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, [interactive]);

  // 2. Desktop Mouse Parallax Handler
  const handleMouseMove = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (!interactive) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const relX = (e.clientX - rect.left) / rect.width; // 0 to 1
    const relY = (e.clientY - rect.top) / rect.height; // 0 to 1

    const normalizedX = (relX - 0.5) * 2; // -1 to +1
    const normalizedY = (relY - 0.5) * 2; // -1 to +1

    setAngle(90 + normalizedX * 30);
    setGlareStop(50 + normalizedX * 25);
    setTilt({ x: normalizedX, y: normalizedY });
  };

  const handleMouseLeave = () => {
    if (!interactive) return;
    setAngle(90);
    setGlareStop(50);
    setTilt({ x: 0, y: 0 });
  };

  const gradientStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(${angle}deg, #00FF41 0%, #FFFFFF ${glareStop}%, #FF2D2D 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    transform: interactive
      ? `perspective(600px) rotateY(${tilt.x * 8}deg) rotateX(${tilt.y * -6}deg)`
      : undefined,
    transition: 'transform 0.15s ease-out',
    display: 'inline-block',
  };

  return (
    <span
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`inline-flex flex-col items-center select-none ${className}`}
    >
      <span
        style={gradientStyle}
        className={`${SIZE_CLASSES[size]} uppercase drop-shadow-[0_0_12px_rgba(0,255,65,0.2)]`}
        aria-label={text}
      >
        {text}
      </span>

      {showAccentBar && (
        <span
          className="flex h-[3px] w-full rounded-full overflow-hidden mt-1 shadow-sm"
          aria-hidden="true"
        >
          <span className="w-1/3 bg-[#00FF41]" />
          <span className="w-1/3 bg-[#FFFFFF]" />
          <span className="w-1/3 bg-[#FF2D2D]" />
        </span>
      )}
    </span>
  );
};

export default TricolorBrandWordmark;
